import { inject, Injectable } from "@angular/core";
import { HttpClient, HttpParams } from "@angular/common/http";
import { Observable } from "rxjs";
import { EntityApiService } from "../../../../shared/store/entity-store/entity-api.service";
import { environment } from "../../../../../environments/environment";
import { Commande } from "../../models/commande.model";
import { CommandeLigne } from "../../models/commande-ligne";
import { CommandeMailPayload } from "../../models/commande-mail-payload.model";
import { CommandeEtat } from "../../enums/etat-commande.enum";
import { safeFirstValueFrom } from "../../../../shared/utils/safe-first-value-from";

@Injectable({ providedIn: "root" })
export class CommandeApiService implements EntityApiService<Commande> {
  httpClient = inject(HttpClient);

  loadOne(id: string): Observable<Commande> {
    return this.httpClient.get<Commande>(
      `${environment.api()}/commandes/eager/${id}`,
    );
  }

  create(entity: Commande): Observable<Commande> {
    return this.httpClient.post<Commande>(
      `${environment.api()}/commandes/create`,
      entity,
    );
  }

  remove(id: string): Observable<string> {
    throw new Error("Method not implemented.");
  }

  update(entity: Commande): Observable<Commande> {
    return this.httpClient.post<Commande>(
      `${environment.api()}/commandes/`,
      entity,
    );
  }

  loadAll(): Observable<Commande[]> {
    return this.httpClient.get<Commande[]>(`${environment.api()}/commandes`);
  }

  async updateCommandeLignes(
    lignes: CommandeLigne[],
    dateLivraisonPrevue: Date,
    commandeId: string,
  ) {
    return await safeFirstValueFrom(
      this.httpClient.post<Commande>(
        `${environment.api()}/commandes/update_lignes/${commandeId}`,
        {
          lignes,
          dateLivraisonPrevue,
        },
      ),
    );
  }

  sendMail(a: CommandeMailPayload) {
    return this.httpClient.post<Commande>(
      `${environment.api()}/commandes/send_mail`,
      a,
    );
  }

  confirm(a: Commande) {
    return this.httpClient.post<Commande>(
      `${environment.api()}/commandes/confirmer`,
      a,
    );
  }

  solder(a: Commande) {
    return this.httpClient.post<Commande>(
      `${environment.api()}/commandes/solder`,
      a,
    );
  }

  desolder(a: Commande) {
    return this.httpClient.post<Commande>(
      `${environment.api()}/commandes/desolder`,
      a,
    );
  }

  terminer(a: Commande) {
    return this.httpClient.post<Commande>(
      `${environment.api()}/commandes/terminer`,
      a,
    );
  }

  reopen(a: Commande) {
    return this.httpClient.post<Commande>(
      `${environment.api()}/commandes/reopen`,
      a,
    );
  }

  cancel(a: Commande) {
    return this.httpClient.post<Commande>(
      `${environment.api()}/commandes/cancel`,
      a,
    );
  }

  uncancel(a: Commande) {
    return this.httpClient.post<Commande>(
      `${environment.api()}/commandes/uncancel`,
      a,
    );
  }

  saveLigne(cl: CommandeLigne) {
    return this.httpClient.post<CommandeLigne>(
      `${environment.api()}/commandes/lignes`,
      cl,
    );
  }

  loadAllFilter(filters: {
    etats: CommandeEtat[];
    etatsReception?: string[];
    search: string;
    sort: string;
    page?: number;
  }) {
    let params = new HttpParams();

    if (filters.sort) {
      params = params.set("sort", filters.sort);
    }
    if (filters.page != null) {
      params = params.set("page", String(filters.page));
    }
    if (filters.etats && filters.etats.length > 0) {
      filters.etats.forEach((etat) => {
        params = params.append("etats", etat.toString());
      });
    }
    if (filters.etatsReception && filters.etatsReception.length > 0) {
      filters.etatsReception.forEach((e) => {
        params = params.append("etatsReception", e);
      });
    }
    if (filters.search) {
      params = params.set("search", filters.search);
    }

    return this.httpClient.get<Commande[]>(
      `${environment.api()}/commandes/navfilter`,
      {
        params,
      },
    );
  }

  async updateDocumentListeId(semId: string, documentListId: string) {
    return await safeFirstValueFrom(
      this.httpClient.post(
        `${environment.api()}/commandes/documentList/${semId}/${documentListId}`,
        null,
      ),
    );
  }

  addMulti(commandes: Commande[]) {
    return this.httpClient.post<Commande[]>(
      `${environment.api()}/commandes/multiple`,
      commandes,
    );
  }
}
